import type { SubscriptionTier } from '@/types/user'
import {
  DEFAULT_TIER_POLICIES,
  type FinanceCalculatorSettings,
  type FinancePolicyTier,
  type FinanceTierPolicy,
} from '@/types/financeSettings'

export interface FinanceVisibilityInput {
  listing: { price?: number | null; financeOptIn?: boolean; categoryGroup?: string }
  seller: {
    subscriptionTier?: SubscriptionTier
    financeSource?: 'good-lending' | 'own' | 'none'
    financeCalculatorEnabled?: boolean
    financeProviderName?: string
    financeMinAmount?: number
  }
  settings: FinanceCalculatorSettings
}

export interface FinanceVisibility {
  show: boolean
  source: 'good-lending' | 'own' | null
}

const POLICY_TIERS: FinancePolicyTier[] = ['general', 'storefront', 'dealership', 'nonprofit']

function policyFor(
  tier: SubscriptionTier | undefined,
  settings: FinanceCalculatorSettings,
): FinanceTierPolicy | null {
  const key = (tier ?? 'general') as FinancePolicyTier
  if (!POLICY_TIERS.includes(key)) return null // 'admin' / unknown → no finance
  const policies = settings.tierPolicies ?? DEFAULT_TIER_POLICIES
  return policies[key] ?? DEFAULT_TIER_POLICIES[key]
}

function goodLendingShows(input: FinanceVisibilityInput): boolean {
  const { listing, seller, settings } = input
  const price = listing.price ?? 0

  if (!settings.enabled) return false
  if (price <= 0) return false

  const policy = policyFor(seller.subscriptionTier, settings)
  if (!policy || policy.mode === 'off') return false

  if (policy.allowedGroups !== 'all') {
    if (!listing.categoryGroup || !policy.allowedGroups.includes(listing.categoryGroup)) return false
  }

  switch (policy.mode) {
    case 'always':
      break
    case 'settings':
      if (seller.financeSource !== 'good-lending') return false
      break
    case 'per-listing':
      if (listing.financeOptIn !== true) return false
      break
  }

  if (price < (settings.minAmount ?? 0)) return false
  return true
}

function ownProviderShows(input: FinanceVisibilityInput): boolean {
  const { listing, seller } = input
  const price = listing.price ?? 0
  if (seller.financeSource !== 'own') return false
  if (price <= 0) return false
  const explicit = seller.financeCalculatorEnabled
  const hasLegacy = !!seller.financeProviderName
  const show = explicit === true || (explicit === undefined && hasLegacy)
  return show
}

/**
 * Single source of truth for whether the finance calculator shows on a listing,
 * and from which config source. Good Lending is gated by the admin tier policy;
 * the dealership "own provider" path is resolved independently (unchanged behaviour).
 */
export function resolveFinanceVisibility(input: FinanceVisibilityInput): FinanceVisibility {
  if (goodLendingShows(input)) return { show: true, source: 'good-lending' }
  if (ownProviderShows(input)) return { show: true, source: 'own' }
  return { show: false, source: null }
}
